import { HttpService } from '../main/HttpService.js';

/*# This class' sole purpose is to communicate with the server. #*/
export class ProductService extends HttpService {
  constructor(serviceUrl) {
    super(serviceUrl);
    this.sameSiteOnly = true; // See super() for info
  }

  async getFormattedTechSpec(langReg, techSpec, signal) {
    return await this.postJsonObject(`FormattedTechSpec?langReg=${langReg}`, techSpec, signal);
  }

  async getVariantProductDetails(variantProductId, variantId, signal) {
    return await this.fetchJson(`VariantProductDetails?variantProductId=${variantProductId}&variantId=${variantId}`, { signal });
  }

  async getProductVariantId(variantProductId, attributes, signal) {
    const dto = {
      variantProductId,
      attributes
    };
    return await this.postJsonObject(`GetProductVariantId`, dto, signal);
  }
}

/*# This class' sole purpose is to communicate with the server. #*/
export class DesignerService extends HttpService {
  constructor(serviceUrl, sessionId) {
    super(serviceUrl);
    this.HDR_SESSION_ID = 'x-designer-session';
    this.sessionId = sessionId;
    this.includeSessionId = true;
  }

  prepareGetOptions(options) {
    options = super.prepareGetOptions(options);
    if (!options) {
      options = {};
    }
    if (!options.headers) {
      options.headers = {};
    }
    if (this.includeSessionId === true) {
      options.headers[this.HDR_SESSION_ID] = this.sessionId;
    }
    return options;
  }

  afterGet(response) {
    super.afterGet(response);
    this.sessionId = response.headers.get(this.HDR_SESSION_ID);
  }

  preparePutAndPostOptions(options) {
    options = super.preparePutAndPostOptions(options);
    if (!options) {
      options = {};
    }
    if (!options.headers) {
      options.headers = {};
    }
    if (this.includeSessionId === true) {
      options.headers[this.HDR_SESSION_ID] = this.sessionId;
    }
    return options;
  }

  afterPost(response) {
    super.afterPost(response);
    this.sessionId = response.headers.get(this.HDR_SESSION_ID);
  }

  async getProductDetails(productId, signal, ignoreSession) {
    if (ignoreSession === true) {
      this.includeSessionId = false;
    }
    try {
      return await this.fetchJson(`details?solutionId=${productId}`, { signal });
    } finally {
      this.includeSessionId = true;
    }
  }

  async updatePropertyValue(name, value, signal) {
    return await this.post(`updatePropertyValue?name=${name}&value=${value}`, signal);
  }

  /* This method should be used if the product contains all product properties, and one need to
   * update/initialize the Designer service's session with a starting point for further product
   * modifications. Each property of the product will be assigned to the product in the service's
   * cached session data.
   *
   * WARNING: This method should only used when the product's properties are "consistent", meaning
   * they have previously been updated/validated by the Designer service. The caller should NOT
   * modify the product's properties and then call this method. Instead one should use the
   * updateProductPlusDelta, updateProductValues, or updateProductValue methods. */
  async updateProduct(product, signal, resetSession) {
    if (resetSession === true) {
      this.includeSessionId = false;
    }
    try {
      return await this.postJsonObject('updateProduct', product, signal);
    } finally {
      this.includeSessionId = true;
    }
  }

  /* This method should be used if the product contains all product properties, and the deltaProduct
   * contains aditional property modifications. The comments for the updateProduct() method apply to
   * the product parameter. */
  async updateProductPlusDelta(product, deltaProduct, signal) {
    const productPlusDelta = { product, deltaProduct };
    return await this.postJsonObject('updateProductPlusDelta', productPlusDelta, signal);
  }

  /* This method should be used if the product contains a subset of all product properties,
   * and it's only this subset which should be updated. */
  async updateProductValues(product, signal) {
    return await this.postJsonObject('updateProductValues', product, signal);
  }
}
